import React, { useCallback, useEffect, useState } from 'react';
import { Card, Badge, FadeIn, FullScreen, Empty } from '../components/ui';
import { rupee } from '../theme';
import BookingProcess from './BookingProcess';
import api from '../api/client';

const tone = { in_progress: 'brand', completed: 'success', pending: 'warning', assigned: 'warning', cancelled: 'neutral' };
// Friendly labels for the staged workflow.
const stageLabel = {
  appointment: 'Appointment due', sourcing: 'Finding workers', interview: 'Interview',
  final: 'Finalizing', security: 'Security due', trial: 'Trial',
  trial_ended: 'Pay to continue', active: 'Active', cancelled: 'Cancelled',
};
const stageTone = {
  appointment: 'warning', sourcing: 'brand', interview: 'brand', final: 'brand',
  security: 'warning', trial: 'brand', trial_ended: 'warning', active: 'success', cancelled: 'neutral',
};

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Server filters by user, so this is always only the logged-in customer's.
  const load = useCallback(() => api.get('/bookings')
    .then((d) => setBookings(d?.bookings || []))
    .catch(() => setBookings([]))
    .finally(() => { setLoading(false); setRefreshing(false); }), []);

  useEffect(() => { load(); }, [load]);
  const onRefresh = () => { setRefreshing(true); load(); };

  return (
    <div className="screen">
      <div className="page-head row-between">
        <div>
          <h1>My Bookings</h1>
          <p>Tap a booking to see details</p>
        </div>
        <button className="chip" onClick={onRefresh} disabled={refreshing}>
          {refreshing ? 'Refreshing…' : '↻ Refresh'}
        </button>
      </div>

      <div className="booking-grid">
        {loading ? (
          <p className="muted center" style={{ marginTop: 40 }}>Loading your bookings…</p>
        ) : bookings.length === 0 ? (
          <Empty icon="🗓️" title="No bookings yet" sub="Your booked services will appear here." />
        ) : bookings.map((b, i) => (
          <FadeIn key={b._id} delay={i * 70}>
            <Card onClick={() => setDetail(b)} style={{ height: '100%' }}>
              <div className="row-between">
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--primary)' }}>{b.bookingCode}</span>
                <Badge
                  label={b.stage ? stageLabel[b.stage] : (b.status || '').replace('_', ' ')}
                  tone={b.stage ? (stageTone[b.stage] || 'neutral') : (tone[b.status] || 'neutral')}
                />
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, marginTop: 10 }}>{b.packageTitle}</div>
              <div className="row-between" style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                <span className="muted" style={{ fontSize: 13 }}>👩‍🔧 {b.staff?.name || 'Assigning…'}</span>
                <span style={{ fontSize: 16, fontWeight: 800 }}>{rupee(b.amount)}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 700, marginTop: 10 }}>View process ›</div>
            </Card>
          </FadeIn>
        ))}
      </div>

      <FullScreen open={!!detail} onClose={() => { setDetail(null); load(); }}>
        {detail && <BookingProcess bookingId={detail._id} onClose={() => { setDetail(null); load(); }} />}
      </FullScreen>
    </div>
  );
}
